import { DeleteOutlineRounded, EditOutlined } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  List,
  ListItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as ProductActions from "../../../../redux/products/products.actions";
import * as ProductReducers from "../../../../redux/products/products.reducers";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import { primary, secondary } from "../../../../themes/theme";
import CustomTooltip from "../../../../utils/CustomTooltip";
import DeleteItemDialog from "../../../../utils/common/delete-item-dialog/DeleteItemDialog";
import SecondarySearchBar from "../../../../utils/common/secondary-search-bar/SecondarySearchBar";
import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import SpinnerUI from "../../../ui/components/spinner/SpinnerUI";

const ManageProduct: React.FC = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [prodIdToDelete, setProdIdToDelete] = useState<string | undefined>("");

  //Get Products data from redux store
  const productReduxState: ProductReducers.InitialState = useSelector(
    (state: RootState) => {
      return state[ProductReducers.productFeatureKey];
    }
  );

  const { loading, products } = productReduxState;

  useEffect(() => {
    dispatch(ProductActions.getAllProductsAction());
  }, []);

  const handleDeleteProduct = (productId: string | undefined) => {
    setDeleteDialogOpen(true);
    setProdIdToDelete(productId);
  };

  const handleDeleteConfirm = () => {
    if (prodIdToDelete) {
      dispatch(
        ProductActions.deleteProductAction({ productId: prodIdToDelete })
      ).then((response: any) => {
        if (response && !response.error) {
          dispatch(ProductActions.getAllProductsAction());
        }
      });
    }
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  //Styles
  const cellStyle1 = {
    color: primary[800],
    fontWeight: "bold",
    fontFamily: "Ubuntu",
  };

  const cellStyle2 = {
    fontFamily: "Ubuntu",
    textTransform: "captilaize",
    Width: "100%",
  };

  return (
    <>
      <Box sx={{ mb: 2, pb: 2 }}>
        {loading && <SpinnerUI />}
        <MainNavBar />
        <LayoutHeading
          heading="Manage Products"
          description="Efficiently oversee product listings, updates, and inventory control for optimal sales."
        />

        {/* Search Bar  */}
        <SecondarySearchBar />

        <Grid
          container
          sx={{ pl: { xs: 1, md: 9, lg: 10 }, pr: { xs: 1, md: 2 } }}
        >
          {products && products.length > 0 ? (
            <>
              <Grid item xs={12}>
                <Typography
                  sx={{ pb: 1, fontFamily: "Ubuntu", fontWeight: "500" }}
                >
                  Total Products : {products.length}
                </Typography>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ borderRadius: "10px" }}
                >
                  <Table>
                    <TableHead sx={{ background: secondary[300] }}>
                      <TableRow>
                        <TableCell sx={{ ...cellStyle1 }}>S.NO</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>Product ID</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>Product</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>Brand</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>Quantity</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map((product, index) => {
                        return (
                          <TableRow key={product._id}>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 50,
                                maxWidth: 60,
                                minWidth: "100%",
                              }}
                            >
                              {index + 1}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 100,
                                maxWidth: 100,
                                minWidth: "100%",
                              }}
                            >
                              {product._id
                                .substring(product._id.length - 5)
                                .toUpperCase()}
                            </TableCell>
                            {/* <TableCell sx={{ ...cellStyle2 }}>
                              <img
                                src={product.imageUrl}
                                alt=""
                                style={{ width: "40px", height: "40px" }}
                              />
                            </TableCell>
                            <TableCell sx={{ ...cellStyle2 }}>
                              {product.title}
                            </TableCell> */}
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 250,
                                minWidth: "100%",
                              }}
                            >
                              <List>
                                <ListItem
                                  onClick={() =>
                                    navigate(
                                      `/products/view/Fashion/${product._id}`
                                    )
                                  }
                                  sx={{ cursor: "pointer", mx: 0, px: 0 }}
                                >
                                  <Grid
                                    container
                                    sx={{
                                      display: "flex",
                                      justifyContent: {
                                        xs: "center",
                                        lg: "flex-start",
                                        height: "100%",
                                        alignItems: "center",
                                      },
                                    }}
                                    spacing={2}
                                  >
                                    <Grid item xs={12} lg={3}>
                                      <img
                                        src={product?.imageUrl}
                                        alt=""
                                        style={{
                                          width: "60px",
                                          height: "60px",
                                          borderRadius: "10px",
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} lg={8}>
                                      <Typography
                                        style={{
                                          display: "-webkit-box",
                                          WebkitLineClamp: 2, // Limit to 2 lines
                                          WebkitBoxOrient: "vertical",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          maxHeight: "3em", // Adjust based on your line height
                                          maxWidth: "6em",
                                          lineHeight: "1.5em", // Adjust based on your preference
                                          fontFamily: "Ubuntu",
                                          fontWeight: "500",
                                          textTransform: "capitalize",
                                        }}
                                      >
                                        {product.title}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </ListItem>
                              </List>
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 120,
                                minWidth: "100%",
                              }}
                            >
                              &#8377;{Number(product.price).toFixed(2)}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 120,
                                minWidth: "100%",
                              }}
                            >
                              {product.quantity}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 120,
                                minWidth: "100%",
                              }}
                            >
                              <CustomTooltip title="Edit" placement="top">
                                <IconButton
                                  size="small"
                                  sx={{
                                    background: "transparent",
                                    color: "green",
                                    borderRadius: "7px",
                                    width: "30px",
                                    height: "30px",
                                    border: "2px solid green",
                                    mr: 1,
                                    "&:hover": {
                                      background: "green",
                                      color: "white",
                                    },
                                  }}
                                  onClick={() =>
                                    navigate(`/products/edit/${product._id}`)
                                  }
                                >
                                  <EditOutlined />
                                </IconButton>
                              </CustomTooltip>
                              <CustomTooltip title="Delete" placement="top">
                                <IconButton
                                  size="small"
                                  sx={{
                                    background: "transparent",
                                    color: "orangered",
                                    borderRadius: "7px",
                                    width: "30px",
                                    height: "30px",
                                    border: "2px solid orangered",
                                    "&:hover": {
                                      background: "orangered",
                                      color: "white",
                                    },
                                  }}
                                  onClick={() =>
                                    handleDeleteProduct(product._id)
                                  }
                                >
                                  <DeleteOutlineRounded />
                                </IconButton>
                              </CustomTooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </>
          ) : (
            <>
              <Box>
                <Typography>No Products Found</Typography>
              </Box>
            </>
          )}
        </Grid>
      </Box>

      {/* Delete Product Dialog  */}
      <DeleteItemDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Product Delete?"
        contentText="Are you sure you want to delete the Product"
      />
    </>
  );
};

export default ManageProduct;
